import React from 'react';
import type { History } from '@repo/types';
import { InventoryService } from '@repo/ui/lib/api/services/inventory.service';
import type { HistoryPdfKind } from './build-history-pdf-snapshot';

export interface DownloadHistoryPrintPdfInput {
  readonly inventaireId: number;
  readonly history: History;
  readonly kind: HistoryPdfKind;
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Generate the same print PDFs as fiche arbre / fiche intervention,
 * using backend print-data with historyId (same source as usePrintData / print arbre).
 */
export async function downloadHistoryPrintPdf(
  input: DownloadHistoryPrintPdfInput,
): Promise<void> {
  const { inventaireId, history, kind } = input;
  const inventoryService = new InventoryService();
  const printData = await inventoryService.getPrintData(inventaireId, history.id);
  if (!printData) {
    throw new Error('Impossible de préparer les données d\'impression historiques');
  }
  const { pdf } = await import('@react-pdf/renderer');
  if (kind === 'fiche_arbre') {
    const { PDFDocument } = await import('@repo/print-pdf/arbre/pdf');
    const { Document } = await import('@react-pdf/renderer');
    const blob = await pdf(
      React.createElement(
        Document,
        null,
        React.createElement(PDFDocument, {
          data: printData as never,
          inventaireId,
        }),
      ),
    ).toBlob();
    triggerBrowserDownload(blob, `fiche-arbre-historique-${history.id}.pdf`);
    return;
  }
  const { generateInterventionPDFDocument } = await import(
    '../../intervention/generate-intervention-pdf'
  );
  const { parsePrintDataGps, generateInterventionStaticMaps } = await import(
    '../../intervention/generate-static-maps'
  );
  const coord = parsePrintDataGps(printData.gps);
  const staticMapsBase64 = coord ? await generateInterventionStaticMaps(coord) : null;
  const documentElement = generateInterventionPDFDocument({
    printData: printData as never,
    inventaireId,
    userSettingsData: null,
    staticMapsBase64: staticMapsBase64 ?? undefined,
  });
  const blob = await pdf(documentElement).toBlob();
  triggerBrowserDownload(blob, `fiche-intervention-historique-${history.id}.pdf`);
}

/** @deprecated Prefer downloadHistoryPrintPdf */
export async function downloadHistoryPdf(
  history: History,
  kind: HistoryPdfKind,
  inventaireId?: number,
): Promise<void> {
  if (!inventaireId) {
    throw new Error('Inventaire ID requis pour générer le PDF print historique');
  }
  await downloadHistoryPrintPdf({
    inventaireId,
    history,
    kind,
  });
}
